import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Loader2, Download, Trash2, X, ChevronRight, Phone, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_LIST = [
    { value: "pending", label: "Pending", tone: "bg-amber-100 text-amber-800 border-amber-200" },
    { value: "dikonfirmasi", label: "Dikonfirmasi", tone: "bg-blue-100 text-blue-800 border-blue-200" },
    { value: "selesai", label: "Selesai", tone: "bg-green-100 text-green-800 border-green-200" },
    { value: "batal", label: "Batal", tone: "bg-red-100 text-red-700 border-red-200" },
];
const statusOf = (v) => STATUS_LIST.find((s) => s.value === v) || STATUS_LIST[0];

export default function AdminOrders() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/orders", { params: filter ? { status: filter } : {} });
            setOrders(data);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    if (user?.role !== "super_admin") {
        return (
            <div className="mx-auto max-w-2xl px-5 py-16 text-center">
                <h1 className="font-heading text-2xl font-semibold">Akses Ditolak</h1>
                <p className="mt-2 text-muted-foreground">Hanya Super Admin yang boleh melihat order.</p>
                <Link to="/admin" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
                    <ArrowLeft size={14} /> Kembali ke Dashboard
                </Link>
            </div>
        );
    }

    const downloadExcel = async () => {
        try {
            const res = await api.get("/admin/orders-export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `orders-${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const updateStatus = async (orderId, newStatus) => {
        try {
            const { data } = await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
            setOrders((arr) => arr.map((o) => (o.id === orderId ? data : o)));
            if (selected?.id === orderId) setSelected(data);
            toast.success(`Status: ${statusOf(newStatus).label}`);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const removeOrder = async (orderId) => {
        if (!window.confirm("Hapus order ini?")) return;
        try {
            await api.delete(`/admin/orders/${orderId}`);
            setOrders((arr) => arr.filter((o) => o.id !== orderId));
            if (selected?.id === orderId) setSelected(null);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div data-testid="admin-orders-page" className="mx-auto max-w-7xl px-5 py-8">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="orders-back-link">
                <ArrowLeft size={14} /> Kembali ke Dashboard
            </Link>
            <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-semibold tracking-tight">Order Masuk</h1>
                    <p className="text-sm text-muted-foreground">Kelola order dari customer & ekspor untuk rekap PO.</p>
                </div>
                <button
                    data-testid="orders-export-btn"
                    onClick={downloadExcel}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary"
                >
                    <Download size={14} strokeWidth={1.75} /> Export Excel
                </button>
            </div>

            <div className="mt-5 flex gap-2 flex-wrap" data-testid="orders-filter-bar">
                <button
                    data-testid="filter-all-btn"
                    onClick={() => setFilter("")}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${filter === "" ? "bg-primary text-primary-foreground" : "border border-border bg-white text-muted-foreground hover:bg-secondary"}`}
                >
                    Semua ({orders.length})
                </button>
                {STATUS_LIST.map((s) => (
                    <button
                        key={s.value}
                        data-testid={`filter-${s.value}-btn`}
                        onClick={() => setFilter(s.value)}
                        className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${filter === s.value ? "bg-primary text-primary-foreground" : "border border-border bg-white text-muted-foreground hover:bg-secondary"}`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 rounded-xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>
                ) : orders.length === 0 ? (
                    <div className="p-10 text-center text-muted-foreground text-sm">Belum ada order{filter ? ` dengan status "${statusOf(filter).label}"` : ""}.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="orders-table">
                        <thead>
                            <tr className="border-b border-border bg-[hsl(var(--muted))] text-left text-xs uppercase tracking-[0.12em] text-muted-foreground">
                                <th className="px-4 py-3 font-medium">No Order</th>
                                <th className="px-4 py-3 font-medium">Tanggal</th>
                                <th className="px-4 py-3 font-medium">Customer</th>
                                <th className="px-4 py-3 font-medium">Items</th>
                                <th className="px-4 py-3 font-medium text-right">Total</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const st = statusOf(o.status);
                                return (
                                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--muted))]/50">
                                        <td className="px-4 py-3 font-mono text-xs font-semibold">{o.order_no}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{o.customer_name}</div>
                                            <div className="text-xs text-muted-foreground">{o.customer_phone}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{o.items.length} item · {o.items.reduce((s, i) => s + i.quantity, 0)} qty</td>
                                        <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatRupiah(o.total)}</td>
                                        <td className="px-4 py-3">
                                            <select
                                                data-testid={`order-status-${o.id}`}
                                                value={o.status}
                                                onChange={(e) => updateStatus(o.id, e.target.value)}
                                                className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider cursor-pointer ${st.tone}`}
                                            >
                                                {STATUS_LIST.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => setSelected(o)} title="Detail" data-testid={`order-detail-${o.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary">
                                                <ChevronRight size={16} />
                                            </button>
                                            <button onClick={() => removeOrder(o.id)} title="Hapus" data-testid={`order-delete-${o.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-destructive hover:bg-destructive/10">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {selected && <OrderDetailDrawer order={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} />}
        </div>
    );
}

function OrderDetailDrawer({ order, onClose, onStatusChange }) {
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white sm:rounded-xl rounded-t-2xl shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="order-detail-drawer">
                <div className="flex items-center justify-between border-b border-border px-5 py-4 shrink-0">
                    <div>
                        <h3 className="font-heading text-lg font-semibold" data-testid="detail-order-no">{order.order_no}</h3>
                        <p className="text-xs text-muted-foreground">{(order.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                    <section>
                        <div className="label-eyebrow text-muted-foreground">Customer</div>
                        <div className="mt-1 font-medium">{order.customer_name}</div>
                        <a href={`https://wa.me/${order.customer_phone.replace('+','')}`} target="_blank" rel="noreferrer" data-testid="detail-customer-wa" className="mt-1 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
                            <Phone size={12} /> {order.customer_phone}
                        </a>
                        {order.customer_address && <div className="mt-1 text-sm text-muted-foreground">{order.customer_address}</div>}
                        {order.notes && (
                            <div className="mt-3 flex gap-2 rounded-md border border-border bg-secondary p-3 text-sm">
                                <FileText size={14} className="shrink-0 mt-0.5 text-muted-foreground" />
                                <span>{order.notes}</span>
                            </div>
                        )}
                    </section>

                    <section>
                        <div className="label-eyebrow text-muted-foreground">Pesanan</div>
                        <div className="mt-2 divide-y divide-border rounded-lg border border-border">
                            {order.items.map((it, i) => {
                                const label = [it.varian, it.sub_varian].filter(Boolean).join(" · ");
                                return (
                                    <div key={i} data-testid={`detail-item-${i}`} className="px-3 py-2.5 flex items-baseline gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{it.product_name}</div>
                                            {label && <div className="text-xs text-muted-foreground">{label}</div>}
                                            {it.sku && <div className="text-[10px] text-muted-foreground font-mono">SKU: {it.sku}</div>}
                                        </div>
                                        <div className="text-xs text-muted-foreground whitespace-nowrap">{it.quantity} × {formatRupiah(it.unit_price)}</div>
                                        <div className="text-sm font-semibold whitespace-nowrap min-w-[80px] text-right">{formatRupiah(it.line_total)}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 flex items-baseline justify-between border-t border-border pt-3">
                            <span className="text-sm text-muted-foreground">Total</span>
                            <span className="font-heading text-xl font-semibold text-primary" data-testid="detail-total">{formatRupiah(order.total)}</span>
                        </div>
                    </section>

                    <section>
                        <div className="label-eyebrow text-muted-foreground">Status</div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {STATUS_LIST.map((s) => (
                                <button
                                    key={s.value}
                                    data-testid={`detail-status-${s.value}`}
                                    onClick={() => onStatusChange(order.id, s.value)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${order.status === s.value ? s.tone : "border-border bg-white text-muted-foreground hover:bg-secondary"}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
